import { FacebookLoginButton } from "@libs/@react-facebook-login";
import { SDK } from "@sdk";
import { useSDKActionWithDeps } from "@sdk/sdk.hooks";
import { Alert, Button, Input, Layout, Typography } from "antd";
import { GetFbAuthScope } from "components/modules/connections/channels/facebook/helpers/get-fb-auth-scope";
import { GlobalConfig } from "config";
import { useCallback, useEffect, useMemo, useState } from "react";

import { iExternalAppType } from "@sdk/external-accounts/external-accounts.service";
import GoogleButton from "react-google-button";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { selectCurrentUserId } from "store/modules/users/users.selectors";
import { useUrlQuery } from "utils/hooks/use-url-query";

const { Content, Footer } = Layout;
const { Title } = Typography;

const supportedTargets = [
  "FACEBOOK",
  "TWITTER_V2",
  "TWITTER_V1",
  "GMAIL",
  "WHATSAPP",
  // App Integrations
  // Payment Providers
  "STRIPE",
  // Task Management
  "NOTION",
  "ASANA",
  "CLICK_UP",
  "JIRA",
  "GIT_HUB",
  "LINEAR",
  // CRM
  "HUB_SPOT",
  "PIPE_DRIVE",
  // Help Desk
  "ZENDESK",
  "FRESH_DESK",
  "ZOHO_DESK",
  // Meetings, and Video Conference
  "AIR_CALL",
  "GOOGLE_MEET",
  "CALENDLY",
  "CAL",
  // Team Communication
  "SLACK",
];

const ExternalAuthConnectionPage = () => {
  // https://web.clickconnector.app/playground?state=state&code=ZmpzUHVHaUhCZ1I2TEdXMEZjekQ1QWxEckxvb1Rlc015eVNWcGdxdXFMcWlwOjE2NTg0NjYzNDYxMTA6MTowOmFjOjE
  // https://auth.clickconnector.io/external-auth-connector?oauth_token=tahzGwAAAAABe0ljAAABgzsVjGU&oauth_verifier=oQ1lm3M1DnzR5Oc2aVBhoHlNYjWc39yq
  const location = useLocation();
  const urlQuery = useUrlQuery();
  const authTarget = urlQuery.get("target") as
    | "FACEBOOK"
    | "WHATSAPP"
    | "TWITTER_V2"
    | "TWITTER_V1"
    | "GMAIL"
    | "D360" // App Integrations
    | iExternalAppType;

  const authCode = urlQuery.get("code")!;
  const oauth_token = urlQuery.get("oauth_token")!;
  const oauth_verifier = urlQuery.get("oauth_verifier")!;
  const oauthState = urlQuery.get("state")!;
  const userId = urlQuery.get("userId")!;

  const requiredFacebookScopes = useMemo(
    () => GetFbAuthScope(authTarget as any),
    [authTarget],
  );

  const sendBackAuthData = useCallback(
    async (
      userId: string,
      data: {
        accessToken?: string;
        code?: string;
        oauth_token?: string;
        oauth_verifier?: string;
        state?: string;
        d360?: {
          channels: string[];
          client: string;
        };
      },
    ) => {
      // Todo: Call HTTP and pass in state
      if (window.opener) {
        window.opener.postMessage(
          {
            externalAuthConnector: data,
            userId: userId,
          },
          "*",
        );
      } else {
        await SDK.twitter.transmitOauthData({
          ...data,
          userId: userId,
        });
      }
      window.close();
    },
    [],
  );

  const windowOpener = useMemo(() => {
    return window.opener;
  }, []);

  useEffect(() => {
    if (authCode) {
      sendBackAuthData(userId || oauthState, {
        code: authCode,
        state: oauthState,
      });
    }
    if (oauth_token && oauth_verifier) {
      sendBackAuthData(userId || oauthState, { oauth_token, oauth_verifier });
    }
  }, [
    authCode,
    oauthState,
    oauth_token,
    oauth_verifier,
    sendBackAuthData,
    userId,
  ]);

  const currentUserId = useSelector(selectCurrentUserId);

  const twitterAuth2URL = useMemo(() => {
    const clientId = "cThWakhKWUFhdHhJa1BSUzU1S246MTpjaQ";
    const redirectUri =
      "https://auth.clickconnector.io/external-auth-connector";
    // const redirectUri = "http://localhost:3000/external-auth-connector";
    const scopes = "tweet.read%20users.read%20offline.access";

    return `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scopes}&state=${currentUserId}&code_challenge=challenge&code_challenge_method=plain`;
  }, [currentUserId]);

  const { doAction: loadTwitterAuthToken, response } = useSDKActionWithDeps(
    () => ({
      action: (SDK) => () => SDK.twitter.getOath1Token(currentUserId),
      // successMessage: "Done",
      failureMessage: "Something went wrong",
      actionDependencies: [],
    }),
    [currentUserId],
  );

  // const { doAction: loadGoogleAuthToken, response: googleAuthResponse } =
  //   useSDKActionWithDeps(
  //     () => ({
  //       action: (SDK) => () => SDK.connections.getGmailAuthUrl(),
  //       // successMessage: "Done",
  //       failureMessage: "Something went wrong",
  //       actionDependencies: [],
  //     }),
  //     []
  //   );

  useEffect(() => {
    if (authTarget === "TWITTER_V1") {
      loadTwitterAuthToken();
    } else if (authTarget === "GMAIL") {
      // loadGoogleAuthToken();
    }
  }, [authTarget, loadTwitterAuthToken]);

  const twitterAuth0URL = useMemo(() => {
    return `https://api.twitter.com/oauth/authorize?oauth_token=${response.oauth_token}`;
  }, [response]);

  // const googleAuthUrl = useMemo(() => {
  //   return `${googleAuthResponse?.authUrl}`;
  // }, [googleAuthResponse?.authUrl]);

  const [isAdBlockerEnabled, setAdBlockerEnabled] = useState(false);

  useEffect(() => {
    if (authTarget === "FACEBOOK") {
      fetch("https://connect.facebook.net/en_US/sdk.js")
        .then(function () {
          console.log("Facebook sdk is allowed:");
          setAdBlockerEnabled(false);
        })
        .catch(function () {
          console.log("Facebook sdk is NOT allowed:");
          setAdBlockerEnabled(true);
          // handle the case that facebook is not allowed
        });
    }
  }, [authTarget]);

  const [subDomain, setSubdomain] = useState("");

  const externAllAuthUrl = useMemo(() => {
    switch (authTarget) {
      case "NOTION": {
        const clientId = "296f2b39-0724-4574-a05e-ff1a99488727";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        const url = `https://api.notion.com/v1/oauth/authorize?client_id=${clientId}&response_type=code&owner=user&redirect_uri=${encodeURIComponent(
          redirectUri,
        )}`;
        return url;
      }
      case "ASANA": {
        const clientId = "1206310343098851";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        const url = `https://app.asana.com/-/oauth_authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(
          redirectUri,
        )}&response_type=code&state=user&code_challenge_method=S256&scope=default`;
        return url;
      }
      case "CLICK_UP": {
        const clientId = "7JMHENHI8X2JP5XHVIXW5PVQLXXJSZ33";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        const url = `https://app.clickup.com/api?client_id=${clientId}&redirect_uri=${encodeURIComponent(
          redirectUri,
        )}`;
        return url;
      }
      case "JIRA": {
        const clientId = "w2OFvs4hPqVe8pgl4heFdxC0F0TmR4dQ";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        const url = `https://auth.atlassian.com/authorize?audience=api.atlassian.com&client_id=${clientId}&scope=read%3Ajira-work%20write%3Ajira-work%20manage%3Ajira-webhook%20read%3Ajira-user%20offline_access&redirect_uri=${redirectUri}%2F&state=test-user&response_type=code&prompt=consent`;
        return url;
      }
      case "GIT_HUB": {
        // const url = `https://github.com/apps/clickconnector`;
        const clientId = "Iv1.08af56adb6daf798";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        const url = `https://github.com/login/oauth/authorize?client_id=${clientId}&state=abcdefg&redirect_uri=${redirectUri}`;
        return url;
      }
      case "LINEAR": {
        // const url = `https://github.com/apps/clickconnector`;
        const clientId = "6afb1a895edd1178cdb689d02b4a2c2a";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        // const redirectUri = "http://localhost:3001/external-auth-connector";
        const url = `https://linear.app/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=&scope=read,issues:create,comments:create&actor=application`;
        return url;
      }
      case "HUB_SPOT": {
        const clientId = "05e3bfd1-e27d-4785-9028-8ae49de6f9db";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        const url = `https://app.hubspot.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=crm.lists.read%20crm.objects.contacts.read%20crm.import%20crm.objects.contacts.write%20crm.schemas.contacts.read%20crm.objects.companies.read%20crm.objects.deals.read%20crm.schemas.companies.read%20crm.schemas.deals.read%20crm.export`;
        return url;
      }
      case "PIPE_DRIVE": {
        const clientId = "5731e24e41a382cf";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        const url = `https://oauth.pipedrive.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}`;
        return url;
      }
      // Help Desk
      case "ZENDESK": {
        const clientId = "zdg-clickconnector";
        // const redirectUri =
        //   "https://auth.clickconnector.io/external-auth-connector";
        const redirectUri = "http://localhost:3001/external-auth-connector";
        const url = `https://${subDomain}.zendesk.com/oauth/authorizations/new?response_type=code&redirect_uri=${redirectUri}&client_id=${clientId}&scope=read%20write&state=${subDomain}`;

        return url;
      }
      case "ZOHO_DESK": {
        const clientId = "1000.ZGO3ULIR3CJLMSEWUMWHSNVX675CBN";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        const url = `https://accounts.zoho.com/oauth/v2/auth?response_type=code&client_id=${clientId}&scope=Desk.tickets.ALL,Desk.contacts.READ,Desk.contacts.WRITE,Desk.search.READ,Desk.settings.READ,Desk.basic.READ&redirect_uri=${redirectUri}&state=-5466400890088961855&access_type=offline`;
        return url;
      }
      case "GOOGLE_MEET": {
        const clientId =
          "504090714122-mku64i7e0f2dv28jbq64uv6tun6tqe1h.apps.googleusercontent.com";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        const url = `https://accounts.google.com/o/oauth2/v2/auth?access_type=offline&scope=https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fcalendar&include_granted_scopes=true&prompt=consent&response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
        return url;
      }
      case "CALENDLY": {
        const clientId = "X4vbQ-I_Zxr3-WvpGqwzB2WB4sopYVwAFW1DtE_MhCk";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        // const redirectUri = "https://3aad36dc978a.ngrok.app";
        const url = `https://auth.calendly.com/oauth/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}`;
        return url;
      }
      case "GMAIL": {
        const clientId =
          "504090714122-mku64i7e0f2dv28jbq64uv6tun6tqe1h.apps.googleusercontent.com";
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";

        const gmailScopes = [
          "https://www.googleapis.com/auth/gmail.readonly",
          "https://www.googleapis.com/auth/gmail.send",
          // "https://www.googleapis.com/auth/gmail.settings.sharing",
          "https://www.googleapis.com/auth/gmail.modify",
        ];
        // https://accounts.google.com/o/oauth2/v2/auth/oauthchooseaccount?access_type=offline&scope=https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fgmail.readonly%20https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fgmail.send&include_granted_scopes=true&prompt=consent&response_type=code&client_id=504090714122-mku64i7e0f2dv28jbq64uv6tun6tqe1h.apps.googleusercontent.com&redirect_uri=https%3A%2F%2Fauth.clickconnector.io%2Fexternal-auth-connector&service=lso&o2v=2&theme=glif&flowName=GeneralOAuthFlow
        const url = `https://accounts.google.com/o/oauth2/v2/auth?access_type=offline&scope=${encodeURIComponent(
          gmailScopes.join(" "),
        )}&include_granted_scopes=true&prompt=consent&response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
        return url;
      }
      case "SLACK": {
        const redirectUri =
          "https://auth.clickconnector.io/external-auth-connector";
        // const redirectUri =
        //   "https://3966-175-157-22-239.ngrok-free.app/external-auth-connector";
        const url = `https://slack.com/oauth/v2/authorize?scope=incoming-webhook&user_scope=&redirect_uri=${encodeURIComponent(
          redirectUri,
        )}&client_id=1825266741926.3017522384710`;
        return url;
      }
    }
    return ``;
  }, [authTarget, subDomain]);

  return (
    <Layout className="layout h-full w-full flex flex-col justify-center items-center">
      <img
        src="/cc-assets/logo.png"
        style={{ width: 100 }}
        alt="ClickConnector"
      />
      <div className="mt-8 bg-white dark:bg-gray-900 p-12 rounded-md  mode_transition">
        {(!windowOpener || !supportedTargets.includes(authTarget)) && (
          <div>It looks like you are lost</div>
        )}

        {windowOpener && (
          <>
            {authTarget === "FACEBOOK" && (
              <div>
                <Title level={3}>Connect Facebook</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>
                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <FacebookLoginButton
                    appId={GlobalConfig.FACEBOOK_APP_ID}
                    size="small"
                    autoLoad={false}
                    fields="name,email,picture"
                    scope={requiredFacebookScopes}
                    callback={(response) => {
                      if (response.accessToken) {
                        sendBackAuthData(userId || oauthState, {
                          accessToken: response.accessToken,
                        });
                      }
                    }}
                    options={{
                      config_id: "1050663686267970",
                      return_scopes: true,
                      // response_type: "code",
                      // override_default_response_type: true,
                      // extras: {
                      //   setup: {
                      //     // Prefilled data can go here
                      //   },
                      // },
                    }}
                    returnScopes={true}
                    authType={"rerequest"}
                    // enable_profile_selector: true,
                    icon={<i className="ri-facebook-box-line"></i>}
                    textButton="Authenticate"
                    // cssClass="rounded-lg kep-login-facebook kep-login-facebook-small"
                  />
                </div>
                {isAdBlockerEnabled && (
                  <div className="mt-4">
                    <Alert
                      message="It looks like you have an ad blocker enabled or blocked FB SDK 🥹"
                      type="warning"
                    />
                  </div>
                )}
              </div>
            )}
            {authTarget === "WHATSAPP" && (
              <div>
                <Title level={3}>Connect WhatsApp Account</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>
                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <FacebookLoginButton
                    appId={GlobalConfig.FACEBOOK_APP_ID}
                    size="small"
                    autoLoad={false}
                    fields="name,email,picture"
                    scope={requiredFacebookScopes}
                    callback={(response) => {
                      console.log("callback response", response);
                      if (response.accessToken) {
                        sendBackAuthData(userId || oauthState, {
                          accessToken: response.accessToken,
                        });
                      } else if (response.code) {
                        sendBackAuthData(userId || oauthState, {
                          code: response.code,
                        });
                      }
                    }}
                    // extras={
                    //   authTarget === "WHATSAPP"
                    //     ? {
                    //         feature: "whatsapp_embedded_signup",
                    //         version: 2,
                    //         setup: {
                    //           // Prefilled data can go here
                    //         },
                    //       }
                    //     : undefined
                    // }
                    options={{
                      config_id: "291354703715591",
                      response_type: "code",
                      override_default_response_type: true,
                      extras: {
                        setup: {
                          // Prefilled data can go here
                        },
                      },
                    }}
                    returnScopes={true}
                    authType={"rerequest"}
                    // enable_profile_selector: true,
                    icon={<i className="ri-facebook-box-line"></i>}
                    textButton="Authenticate"
                    // cssClass="rounded-lg kep-login-facebook kep-login-facebook-small"
                  />
                </div>
                <Alert
                  type="info"
                  showIcon
                  message="Business already linked to embedded signup?"
                  description={`If you get message "Business already linked to embedded signup", You can simply close the authentication window to continue the setup.`}
                  style={{ maxWidth: 400 }}
                  className="mt-4"
                />
                {isAdBlockerEnabled && (
                  <div className="mt-4">
                    <Alert
                      message="It looks like you have an ad blocker enabled or blocked FB SDK 🥹"
                      type="warning"
                    />
                  </div>
                )}
              </div>
            )}

            {authTarget === "TWITTER_V2" && (
              <div>
                <Title level={3}>Connect Twitter</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-twitter-line"></i>}
                    href={twitterAuth2URL}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "TWITTER_V1" && (
              <div>
                <Title level={3}>Connect Twitter</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-twitter-line"></i>}
                    href={twitterAuth0URL}
                    loading={Boolean(!response?.oauth_token)}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "GMAIL" && (
              <div>
                <Title level={3}>Connect Gmail</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <GoogleButton
                    onClick={() => {
                      window.location.href = externAllAuthUrl;
                    }}
                    // disabled={Boolean(!googleAuthResponse?.authUrl)}
                  />

                  {/* <Button
                    type="primary"
                    icon={<i className="ri-google-line"></i>}
                    href={googleAuthUrl}
                    loading={Boolean(!googleAuthResponse?.authUrl)}
                  >
                    Authenticate
                  </Button> */}
                </div>
              </div>
            )}

            {authTarget === "D360" && (
              <div>
                <Title level={3}>Connect Whatsapp Account</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  Not Supported Anymore
                </div>
              </div>
            )}

            {authTarget === "NOTION" && (
              <div>
                <Title level={3}>Connect Notion</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "ASANA" && (
              <div>
                <Title level={3}>Connect Asana</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "CLICK_UP" && (
              <div>
                <Title level={3}>Connect Click Up</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "JIRA" && (
              <div>
                <Title level={3}>Connect Jira</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "GIT_HUB" && (
              <div>
                <Title level={3}>Connect Git Hub</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "LINEAR" && (
              <div>
                <Title level={3}>Connect Linear</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "HUB_SPOT" && (
              <div>
                <Title level={3}>Connect HubSpot</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "PIPE_DRIVE" && (
              <div>
                <Title level={3}>Connect Pipe Drive</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {/* Help Desk */}
            {authTarget === "ZENDESK" && (
              <div>
                <Title level={3}>Connect Zen Desk</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="font-bold mb-2">Enter Your Zen Desk Domain</div>

                {/* Eg: d3v-clickconnector */}
                <Input
                  addonAfter=".zendesk.com"
                  addonBefore="https://"
                  value={subDomain}
                  onChange={(e) => setSubdomain(e.target.value)}
                  placeholder="Sub Domain"
                />

                <div className="authentication-button-container flex flex-row justify-center items-center mt-8">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "ZOHO_DESK" && (
              <div>
                <Title level={3}>Connect Zoho Desk</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "GOOGLE_MEET" && (
              <div>
                <Title level={3}>Connect Google Calendar</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <GoogleButton
                    onClick={() => {
                      window.location.href = externAllAuthUrl;
                    }}
                  />
                </div>
              </div>
            )}
            {authTarget === "CALENDLY" && (
              <div>
                <Title level={3}>Connect Calendly</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
            {authTarget === "SLACK" && (
              <div>
                <Title level={3}>Connect Slack</Title>
                <div className="icon-container text-10xl py-4 flex flex-row justify-center items-center">
                  <i className="ri-fingerprint-line"></i>
                </div>

                <div className="authentication-button-container flex flex-row justify-center items-center">
                  <Button
                    type="primary"
                    icon={<i className="ri-arrow-right-line"></i>}
                    href={externAllAuthUrl}
                  >
                    Authenticate
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <Footer style={{ textAlign: "center" }}>
        ClickConnector ©{new Date().getFullYear()}
      </Footer>
    </Layout>
  );
};

export default ExternalAuthConnectionPage;
